import React from 'react';
import styled from 'styled-components';
import ConfirmationBar from './shared/confirmation_bar';
import SlotPicker from './shared/slot_picker';
import DatePicker from './shared/date_picker';
import TimeZonePicker from './shared/time_zone_picker';
import InterviewDetails from './mobile/interview_details';
import { withLocalePrefix } from 'shared/utils/translation';
import classNames from 'classnames';
import { SectionHeader } from '@grnhse/seedling/lib/azalea/typography';
import SubmitSpinner from './shared/submit_spinner';
import CandidateViewShell from './shared/candidate_view_shell';
import type { Props } from './shared/candidate_view_shell';
import AdditionalDetails from './shared/additional_details';
import ArrowUpIcon from '@grnhse/seedling/lib/birch/icons/arrow_up';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import { PrimaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { SectionHeader as BonsaiSectionHeader } from '@grnhse/bonsai/lib/components/typography';
import { PrimaryButton as BonsaiPrimaryButton } from '@grnhse/bonsai/lib/components/buttons';
import { Bonsai } from '@grnhse/bonsai/lib/core';
const t = withLocalePrefix('self_schedule.candidate_view');

const transitionTime = 500;

export default class CandidateView extends CandidateViewShell {
  constructor(props: Props) {
    super(props);
    this.state = {
      ...this.state,
      currentScreen: 0,
      prevScreen: null,
    };
  }

  transitionScreen = (
    prevScreen: number | null | undefined,
    currentScreen: number,
    duration: number
  ) => {
    this.setState({
      currentScreen,
    });

    setTimeout(() => {
      this.setState({
        prevScreen,
      });
    }, duration);
  };

  renderUpArrow = (
    prevScreen: number | null | undefined,
    currentScreen: number
  ) => (
    <IconWrapper
      onClick={() => {
        this.transitionScreen(prevScreen, currentScreen, transitionTime);
      }}
    >
      <ArrowUpIcon />
    </IconWrapper>
  );

  renderDownArrow = (
    prevScreen: number | null | undefined,
    currentScreen: number
  ) => (
    <IconWrapper
      onClick={() => {
        this.transitionScreen(prevScreen, currentScreen, transitionTime / 2);
      }}
    >
      <ArrowDownIcon />
    </IconWrapper>
  );

  render() {
    const {
      candidatePronunciation,
      candidatePronouns,
      jobTitle,
      interviewName,
      interviewLength,
      interviewLocation,
      interviewInvitation,
      timeZones,
      useVideo,
      videoConferencingProvider,
      start,
      end,
      isMobile,
      footer,
      isReschedule,
      bonsaiEnabled,
      bonsaiProps,
      logoUrl,
    } = this.props;
    const {
      submitting,
      selectedTimeZoneOption,
      selectedDate,
      currentMonth,
      selectedTime,
      currentScreen,
      prevScreen,
      availableSlotsByDay,
    } = this.state;

    const additionalDetails =
      candidatePronunciation.display || candidatePronouns.display;

    return (
      <Bonsai configuration={bonsaiProps || {}}>
        <div id="touch_screen_availability_widget">
          <InterviewDetails
            jobTitle={jobTitle}
            interviewName={interviewName}
            interviewLength={interviewLength}
            interviewLocation={interviewLocation}
            interviewInvitation={interviewInvitation}
            logoUrl={logoUrl}
            useVideo={useVideo}
            videoConferencingProvider={videoConferencingProvider}
            bonsaiEnabled={bonsaiEnabled}
          />
          <MainContent
            bonsaiEnabled={bonsaiEnabled}
            hasLogo={!!logoUrl}
            isFirstScreen={currentScreen === 0}
          >
            <Screen
              screen="0"
              className={classNames('animate', {
                translateY: currentScreen !== 0,
              })}
            >
              <Title bonsaiEnabled={bonsaiEnabled}>
                {t('mobile.select_date')}
              </Title>
              <DatePicker
                availableSlotsByDay={availableSlotsByDay}
                currentMonth={currentMonth}
                end={end}
                selectedDate={selectedDate}
                selectDate={(date) => {
                  this.setState({
                    selectedDate: date,
                    prevScreen: 0,
                  });

                  setTimeout(() => {
                    this.setState({
                      currentScreen: 1,
                      selectedDate: date,
                      selectedTime: null,
                    });
                  }, transitionTime / 2);
                }}
                setCurrentMonth={(month) => {
                  this.setState({ currentMonth: month });
                }}
                start={start}
                isMobile={isMobile}
                bonsaiEnabled={bonsaiEnabled}
              />
              {selectedDate && this.renderDownArrow(0, 1)}
            </Screen>
            <Screen
              screen="1"
              className={classNames('animate', {
                hidden: currentScreen === 0,
                visible: currentScreen === 1,
                translateY: currentScreen === 2,
                displayNone: prevScreen === null || currentScreen === 3,
              })}
            >
              {this.renderUpArrow(null, 0)}
              <Title bonsaiEnabled={bonsaiEnabled}>
                {t('mobile.select_time')}
              </Title>
              <SlotPicker
                availableSlotsByDay={availableSlotsByDay}
                selectTime={(slot) => {
                  this.setState({ selectedTime: slot, prevScreen: 1 });

                  setTimeout(() => {
                    this.setState({ currentScreen: 2 });
                  }, transitionTime / 2);
                }}
                selectedTime={selectedTime}
                selectedTimeZoneOption={selectedTimeZoneOption}
                selectedDate={selectedDate}
                bonsaiEnabled={bonsaiEnabled}
              />
              <TimeZoneContainer>
                <TimeZonePicker
                  timeZones={timeZones}
                  selectTimeZoneOption={(timeZoneOption) => {
                    this.setState({ selectedTimeZoneOption: timeZoneOption });
                  }}
                  selectedTimeZoneOption={selectedTimeZoneOption}
                />
              </TimeZoneContainer>
              {selectedTime && this.renderDownArrow(1, 2)}
            </Screen>
            <Screen
              screen="2"
              className={classNames('animate', {
                hidden: currentScreen !== 2,
                visible: currentScreen === 2,
                translateY: currentScreen === 3,
                // displayNone: prevScreen !== 1 && currentScreen !== 2
              })}
            >
              {submitting && <SubmitSpinner size="medium" />}
              {this.renderUpArrow(2, 1)}
              <ConfirmationBar
                isMobile={isMobile}
                interviewLength={interviewLength}
                selectedTime={selectedTime}
                selectedTimeZoneOption={selectedTimeZoneOption}
                submitting={submitting}
                next={additionalDetails}
                isReschedule={isReschedule}
                submit={() => {
                  if (additionalDetails) {
                    // store details in state
                    this.setState({ prevScreen: 2 });

                    setTimeout(() => {
                      this.setState({ currentScreen: 3 });
                    }, transitionTime / 2);
                  } else {
                    this.submit();
                  }
                }}
                bonsaiEnabled={bonsaiEnabled}
              />
            </Screen>
            <Screen
              screen="3"
              className={classNames('animate', {
                hidden: currentScreen !== 3,
                visible: currentScreen === 3,
                displayNone: prevScreen !== 2,
              })}
            >
              {this.renderUpArrow(3, 2)}
              <AdditionalDetails
                candidatePronunciation={candidatePronunciation}
                candidatePronouns={candidatePronouns}
                isMobile={isMobile}
                footer={footer}
                bonsaiEnabled={bonsaiEnabled}
              >
                {bonsaiEnabled ? (
                  <BonsaiPrimaryButton
                    ariaLabel={t('mobile.submit_details')}
                    disabled={!selectedTime || submitting}
                    onClick={() => this.submit()}
                  >
                    {t('mobile.submit_details')}
                  </BonsaiPrimaryButton>
                ) : (
                  <StyledButton
                    isMobile={isMobile}
                    disabled={!selectedTime || submitting}
                    onClick={() => this.submit()}
                  >
                    {t('mobile.submit_details')}
                  </StyledButton>
                )}
              </AdditionalDetails>
            </Screen>
          </MainContent>
        </div>
      </Bonsai>
    );
  }
}

const MainContent = styled.div<{
  bonsaiEnabled: boolean;
  hasLogo: boolean;
  isFirstScreen: boolean;
}>`
  padding-top: ${({ bonsaiEnabled, hasLogo, isFirstScreen }) => {
    if (bonsaiEnabled && hasLogo) {
      return isFirstScreen ? '200px' : '175px';
    }

    return isFirstScreen ? '125px' : '175px';
  }};
  padding-bottom: ${({ isFirstScreen }) => (isFirstScreen ? '0' : '100px')};
  overflow-x: hidden;

  // Mobile phones with a viewport below this will need to scrolling
  // to see the full calendar
  @media only screen and (max-width: 359px) {
    overflow-x: scroll;
  }
`;

const SelectTimeSectionHeader = styled(SectionHeader)`
  margin: 0 25px;
`;

const Title = ({
  children,
  bonsaiEnabled,
}: {
  children: React.ReactNode;
  bonsaiEnabled?: boolean;
}) => {
  if (bonsaiEnabled) {
    return (
      <div style={{ margin: '0 25px' }}>
        <BonsaiSectionHeader>{children}</BonsaiSectionHeader>
      </div>
    );
  }

  return <SelectTimeSectionHeader>{children}</SelectTimeSectionHeader>;
};

const Screen = styled.div<{
  screen: string;
}>`
  &.animate {
    transition: margin ${transitionTime}ms ease,
      opacity ${transitionTime}ms ease;
  }

  &.translateY {
    margin-top: ${({ screen }) => (screen === '2' ? '-170px' : '-400px')};
    opacity: 0;
    pointer-events: none;
  }

  &.displayNone {
    display: none;
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }

  &.visible {
    opacity: 1;
  }
`;

const StyledButton = styled(PrimaryButton)<{ isMobile: boolean }>`
  display: inline-block;

  ${({ isMobile }) =>
    isMobile &&
    ` width: 100%;
      text-align: center;
      display: block;
  `}
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimeZoneContainer = styled.div`
  font-size: 16px;
  margin-bottom: 24px;
`;
